import { useState } from "react";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";
import { DestinatariosEditor, destinatarioVacio, destinatarioDesdeApi, destinatariosParaGuardar } from "@/components/admin/DestinatariosEditor";

const MAX_DESTINATARIOS_CLIENTE = 10;

export default function Clientes() {
  usePageTitle("Clientes");
  const queryClient = useQueryClient();
  const [dialogo, setDialogo] = useState(null); // { modo: 'nuevo'|'editar', cliente? }
  const [confirmar, setConfirmar] = useState(null); // { tipo: 'eliminar'|'activar', cliente }

  const { data: clientes, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-clientes"],
    queryFn: () => adminApi.get("/admin/clientes").then((r) => r.data),
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ["admin-clientes"] });

  const eliminarMutation = useMutation({
    mutationFn: (cliente) => adminApi.delete(`/admin/clientes/${cliente.id}`),
    onSuccess: () => { toast.success("Cliente eliminado"); invalidar(); },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmar(null),
  });

  const activarMutation = useMutation({
    mutationFn: (cliente) => adminApi.put(`/admin/clientes/${cliente.id}/activar`, null, { params: { activo: !cliente.activo } }),
    onSuccess: (_res, cliente) => {
      toast.success(cliente.activo ? "Cliente desactivado" : "Cliente activado");
      invalidar();
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmar(null),
  });

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--tc-text)]">Clientes corporativos</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Solo visible para administradores.</p>
        </div>
        <button
          data-testid={ADMIN.clientesNewButton}
          onClick={() => setDialogo({ modo: "nuevo" })}
          className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
        >
          <Plus size={16} /> Nuevo cliente
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} /></div>
        ) : isError ? (
          <ErrorState testId="admin-clientes-error" mensaje="No se pudo cargar el listado de clientes." onRetry={refetch} />
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Building2 size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">Todavía no hay clientes corporativos.</p>
          </div>
        ) : (
          <table className="w-full min-w-[640px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead>
              <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Unidades</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c) => (
                <tr key={c.id} data-testid={ADMIN.clientesRow} className="border-b border-[var(--tc-border)] last:border-0">
                  <td className="px-4 py-3 font-medium text-[var(--tc-text)]">{c.nombre}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--tc-text-secondary)]">{c.slug}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{c.total_unidades}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c.activo ? "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]" : "bg-gray-100 text-gray-500"}`}>
                      {c.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={ADMIN.clientesEditButton} onClick={() => setDialogo({ modo: "editar", cliente: c })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                        <Pencil size={15} />
                      </button>
                      <button data-testid={ADMIN.clientesToggleActivoButton} onClick={() => setConfirmar({ tipo: "activar", cliente: c })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                        {c.activo ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                      </button>
                      <button data-testid={ADMIN.clientesDeleteButton} onClick={() => setConfirmar({ tipo: "eliminar", cliente: c })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-red-50 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogo && (
        <ClienteDialog
          dialogo={dialogo}
          onClose={() => setDialogo(null)}
          onSaved={() => { setDialogo(null); invalidar(); }}
        />
      )}

      <AlertDialog open={Boolean(confirmar)} onOpenChange={(v) => !v && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmar?.tipo === "eliminar" ? "¿Eliminar cliente?" : confirmar?.cliente?.activo ? "¿Desactivar cliente?" : "¿Activar cliente?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmar?.tipo === "eliminar"
                ? `Se eliminará "${confirmar?.cliente?.nombre}". Si tiene unidades cargadas, no se podrá eliminar y te sugeriremos desactivarlo.`
                : `"${confirmar?.cliente?.nombre}" quedará ${confirmar?.cliente?.activo ? "inactivo" : "activo"}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ADMIN.confirmDialogConfirm}
              onClick={() => confirmar.tipo === "eliminar" ? eliminarMutation.mutate(confirmar.cliente) : activarMutation.mutate(confirmar.cliente)}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ClienteDialog({ dialogo, onClose, onSaved }) {
  const cliente = dialogo.cliente;
  const [nombre, setNombre] = useState(cliente?.nombre || "");
  const [slug, setSlug] = useState(cliente?.slug || "");
  const [slugTocado, setSlugTocado] = useState(Boolean(cliente));
  const [destinatarios, setDestinatarios] = useState(
    cliente?.destinatarios?.length ? cliente.destinatarios.map(destinatarioDesdeApi) : [destinatarioVacio()]
  );
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onNombreChange = (v) => {
    setNombre(v);
    if (!slugTocado) setSlug(v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const body = {
      nombre, slug, logo_url: cliente?.logo_url,
      destinatarios: destinatariosParaGuardar(destinatarios),
    };
    try {
      if (dialogo.modo === "editar") {
        await adminApi.put(`/admin/clientes/${cliente.id}`, body);
      } else {
        await adminApi.post("/admin/clientes", body);
      }
      toast.success("Cliente guardado");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogo.modo === "editar" ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          <DialogDescription>Datos del cliente corporativo y, opcionalmente, a quiénes avisar por WhatsApp o email cuando sus unidades cambien de etapa.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Nombre</label>
            <input data-testid={ADMIN.clienteFormNombre} required value={nombre} onChange={(e) => onNombreChange(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Slug</label>
            <input data-testid={ADMIN.clienteFormSlug} required value={slug} onChange={(e) => { setSlugTocado(true); setSlug(e.target.value); }} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 font-mono text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>

          <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-[var(--tc-surface)] p-3.5">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--tc-text-secondary)]">Destinatarios de notificación (opcional, hasta {MAX_DESTINATARIOS_CLIENTE})</p>
            <div className="mt-2.5">
              <DestinatariosEditor destinatarios={destinatarios} onChange={setDestinatarios} max={MAX_DESTINATARIOS_CLIENTE} />
            </div>
          </div>

          {error && <p data-testid={ADMIN.clienteFormError} className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.clienteFormSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
